import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { ENTITLEMENTS, isEntitlement, type Entitlement } from './entitlements'

/**
 * Who has what.
 *
 * **Not specified by the plan.** Phase 1b Task 3's Interfaces line promises
 * `useEntitlement(e): boolean`, its Step 3 implements only `ENTITLEMENTS` and
 * the type, and Task 4 then imports this hook from a module nothing creates.
 * The data source is named nowhere, so this supplies the smallest shape that
 * satisfies the promise without inventing a contract: a context carrying the
 * granted set, defaulting to none.
 *
 * The real source is the RevenueCat Web SDK, keyed on `UserResponse.id`
 * **uppercased** (CLAUDE.md). `billing/BillingProvider` (#102) owns this
 * provider in the app and feeds `granted` from the build's tier plus the
 * signed-in user's RevenueCat entitlements; with no usable key it passes the
 * tier alone. No call site changed.
 *
 * `loading` says the grant is not known YET (billing is still reading customer
 * info). `useEntitlement` stays closed throughout; only `useEntitlementsLoading`
 * lets a surface avoid announcing a lock that may not be real.
 *
 * **Closed by default, deliberately.** Outside a provider, or before one has
 * loaded, every gate reads locked. An open-by-default gate fails silently in
 * the direction that gives away paid features.
 */

const EntitlementsContext = createContext<readonly Entitlement[]>([])
const LoadingContext = createContext(false)

export function EntitlementsProvider({
  granted,
  loading = false,
  children,
}: {
  /** Accepts raw strings because this is where server data lands — unknown
   *  names are dropped rather than trusted or thrown on. */
  granted: readonly string[]
  /** The grant is not known yet. `useEntitlement` still reads closed. */
  loading?: boolean
  children: ReactNode
}) {
  // Keyed on the joined value, not the array identity: a caller passing a fresh
  // literal each render would otherwise produce a new context value every time
  // and re-render every gate in the app.
  //
  // The memo derives from `key` rather than closing over `granted`, so the
  // dependency array is complete and honest — no suppressed lint rule. Reading
  // the array instead would make `granted` a real dependency the array omits,
  // which is the thing CLAUDE.md's bar forbids papering over.
  //
  // **Narrow BEFORE joining.** Filtering only after the split let a single
  // unrecognised value forge two real ones: `['ai_features|export_data']` is
  // one string, not an entitlement, and should be dropped — but it joined to
  // `ai_features|export_data`, split back into two names, and both passed
  // `isEntitlement`. That is a gate opening on input designed not to open it,
  // which is the direction that gives away paid features. Narrowing first means
  // the split can only ever yield names already validated.
  const key = [...new Set(granted.filter(isEntitlement))].sort().join('|')
  const value = useMemo(() => key.split('|').filter(isEntitlement), [key])

  return (
    <EntitlementsContext.Provider value={value}>
      <LoadingContext.Provider value={loading}>{children}</LoadingContext.Provider>
    </EntitlementsContext.Provider>
  )
}

/** True while the grant is still being read — not "locked", just "not known". */
export function useEntitlementsLoading(): boolean {
  return useContext(LoadingContext)
}

/** True if the current user has this entitlement. */
export function useEntitlement(entitlement: Entitlement): boolean {
  return useContext(EntitlementsContext).includes(entitlement)
}

/** The whole granted set, already narrowed to the seven. */
export function useEntitlements(): readonly Entitlement[] {
  return useContext(EntitlementsContext)
}

/** Re-exported so a call site needs one import, not two. */
export { ENTITLEMENTS, type Entitlement }
